/**
 * Fetches company credits and plan from the real backend only.
 * Same API as Admin CreditManagement – no mock / no local storage fallback.
 */
import { useState, useEffect, useCallback } from 'react';
import { companyCreditsApi } from './companyCreditsApi';

const defaultSummary = {
  plan_id: null,
  plan_name: null,
  plan_validity_days: null,
  credit_balance: 0,
  credits_available: 0,
  credits_blocked: 0,
  credits_used: 0,
  credits_total: 0,
  renewal_date: null,
  price: null,
  is_custom_plan: false,
  custom_plan_auto_renew: false,
};

function mapResponseToSummary(data) {
  if (!data || typeof data !== 'object') return defaultSummary;
  const balance = Number(data.credit_balance ?? data.credits_available ?? 0) || 0;
  return {
    ...defaultSummary,
    plan_id: data.plan_id ?? null,
    plan_name: data.plan_name ?? null,
    plan_validity_days: data.plan_validity_days ?? data.validity_days ?? null,
    credit_balance: balance,
    credits_available: data.credits_available ?? balance,
    credits_blocked: Number(data.credits_blocked ?? 0) || 0,
    credits_used: Number(data.credits_used ?? 0) || 0,
    credits_total: Number(data.credits_total ?? 0) || 0,
    renewal_date: data.renewal_date ?? null,
    price: (data.price ?? data.plan_price) != null ? Number(data.price ?? data.plan_price) : null,
    is_custom_plan: Boolean(data.is_custom_plan),
    custom_plan_auto_renew: Boolean(data.custom_plan_auto_renew),
  };
}

/**
 * Uses only the real backend (GET /api/credits/companies/:id).
 * On error or no company id: sets error, no mock data.
 */
export function useCompanyCredits() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(defaultSummary);

  const fetchSummary = useCallback(async () => {
    const companyId = companyCreditsApi.getCompanyId();
    if (!companyId) {
      setError(new Error('Not logged in as a company'));
      setSummary(defaultSummary);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await companyCreditsApi.getSummary(companyId);
      setSummary(mapResponseToSummary(res.data));
    } catch (err) {
      setError(err);
      setSummary(defaultSummary);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { loading, error, summary, refetch: fetchSummary };
}
